/**
 * The repository's icon set.
 *
 * One reason this file exists: the authoring toolbar drew its controls with
 * Unicode dingbats — `⯇ ≡ ⯈ ☰ ▨ ▦ ⤓ ▤ ⧉ ⌦▦ ⇥ ⇤`. Which of those a reader
 * actually sees depends on the fonts installed on their machine: several are
 * absent from the default Windows stack and render as a missing-glyph box, and
 * the ones that do render are drawn at whatever weight and baseline that font
 * chose, so a row of them never lines up. They also cannot take a colour or a
 * size the way the rest of the interface does.
 *
 * These are stroked paths on a 16px grid at 1.4–1.5 weight, `currentColor`
 * throughout, so a control's colour comes from its state and nothing has to be
 * restyled per icon.
 *
 * Shared between the Add-documents panel and the editor deliberately: the two
 * are one journey, and a folder or a chevron that differs between them reads as
 * two different products.
 */

import type { CSSProperties } from 'react'

export type IconName =
  | 'chevronLeft' | 'chevronDown' | 'chevronRight' | 'arrowRight'
  | 'info' | 'check' | 'close' | 'plus' | 'more' | 'alert' | 'alertCircle'
  | 'lock' | 'folder' | 'pencil' | 'upload' | 'download' | 'history'
  | 'page' | 'compare' | 'trash'
  | 'highlight' | 'link' | 'image' | 'imageWide' | 'imageHalf'
  | 'alignLeft' | 'alignCenter' | 'alignRight' | 'alignJustify'
  | 'bulletList' | 'orderedList' | 'indent' | 'outdent'
  | 'table' | 'rowAfter' | 'columnAfter' | 'mergeCells'
  | 'pageBreak' | 'controlBlock' | 'rule'
  | 'undo' | 'redo'

/**
 * Path geometry only. Every icon shares one viewBox and one stroke setup, so a
 * new one is a `d` attribute rather than another set of decisions.
 */
const PATHS: Record<IconName, React.ReactNode> = {
  chevronLeft: <path d="M10 3 5.5 8 10 13" />,
  chevronRight: <path d="M6 3l4.5 5L6 13" />,
  chevronDown: <path d="M4 6.5 8 10.5 12 6.5" />,
  arrowRight: <><path d="M3 8h9.4" /><path d="m8.6 4.2 3.8 3.8-3.8 3.8" /></>,
  info: <><path d="M8 7.4v4.2" /><path d="M8 4.6v.2" /><circle cx="8" cy="8" r="5.8" /></>,
  check: <path d="m3.4 8.4 3 3 6.2-6.6" />,
  close: <><path d="M4.2 4.2 11.8 11.8" /><path d="M11.8 4.2 4.2 11.8" /></>,
  plus: <><path d="M8 3.4v9.2" /><path d="M3.4 8h9.2" /></>,
  more: <><circle cx="3.4" cy="8" r="1.05" fill="currentColor" stroke="none" /><circle cx="8" cy="8" r="1.05" fill="currentColor" stroke="none" /><circle cx="12.6" cy="8" r="1.05" fill="currentColor" stroke="none" /></>,
  alert: <><path d="M8 2.6 14.4 13.4H1.6z" /><path d="M8 6.6v3" /><path d="M8 11.4v.2" /></>,
  alertCircle: <><circle cx="8" cy="8" r="5.8" /><path d="M5.6 5.6 10.4 10.4" /></>,
  lock: <><rect x="3.2" y="7" width="9.6" height="6.4" rx="1.4" /><path d="M5.4 7V5.2a2.6 2.6 0 0 1 5.2 0V7" /></>,
  folder: <path d="M2.4 5.2A1.4 1.4 0 0 1 3.8 3.8h2.6l1.3 1.6h4.5a1.4 1.4 0 0 1 1.4 1.4v5a1.4 1.4 0 0 1-1.4 1.4H3.8a1.4 1.4 0 0 1-1.4-1.4z" />,
  pencil: <path d="M10.4 2.6 13.4 5.6 6.2 12.8 2.6 13.4l.6-3.6z" />,
  upload: <><path d="M8 11V3" /><path d="m5 6 3-3 3 3" /><path d="M2.8 10.6v1.8a1.6 1.6 0 0 0 1.6 1.6h7.2a1.6 1.6 0 0 0 1.6-1.6v-1.8" /></>,
  download: <><path d="M8 2.6v7" /><path d="m5 6.8 3 3 3-3" /><path d="M3 13.2h10" /></>,
  history: <><path d="M2.6 8a5.4 5.4 0 1 0 1.7-3.9" /><path d="M2.4 2.4v3.4h3.4" /><path d="M8 5.2V8l2 1.4" /></>,
  page: <><path d="M9.2 1.8H4.2a1.4 1.4 0 0 0-1.4 1.4v9.6a1.4 1.4 0 0 0 1.4 1.4h7.6a1.4 1.4 0 0 0 1.4-1.4V5.8z" /><path d="M9.2 1.8v4h4" /></>,
  compare: <><path d="M6.6 3.4H4.2a1.4 1.4 0 0 0-1.4 1.4v6.4a1.4 1.4 0 0 0 1.4 1.4h2.4" /><path d="M9.4 3.4h2.4a1.4 1.4 0 0 1 1.4 1.4v6.4a1.4 1.4 0 0 1-1.4 1.4H9.4" /><path d="M8 2v12" /></>,
  trash: <><path d="M2.8 4.6h10.4" /><path d="M6.4 4.6V3.2h3.2v1.4" /><path d="m4.2 4.6.7 8.2h6.2l.7-8.2" /></>,
  highlight: <><path d="M9.5 2.8 13.2 6.5 7.4 12.3H3.7V8.6z" /><path d="M2.6 14.4h10.8" /></>,
  link: <><path d="M6.8 9.2a2.6 2.6 0 0 0 3.7 0l2-2a2.6 2.6 0 0 0-3.7-3.7l-.7.7" /><path d="M9.2 6.8a2.6 2.6 0 0 0-3.7 0l-2 2a2.6 2.6 0 0 0 3.7 3.7l.7-.7" /></>,
  image: <><rect x="2.4" y="3.4" width="11.2" height="9.2" rx="1.4" /><circle cx="6" cy="6.6" r="1.1" /><path d="m2.9 11.4 3.3-3 3 2.4 1.7-1.4 2.6 2.3" /></>,
  imageWide: <><rect x="1.6" y="4.4" width="12.8" height="7.2" rx="1.2" /><path d="M1.6 2.2h12.8" /><path d="M1.6 13.8h12.8" /></>,
  imageHalf: <><rect x="4.6" y="4.4" width="6.8" height="7.2" rx="1.2" /><path d="M1.6 2.2h12.8" /><path d="M1.6 13.8h12.8" /></>,
  alignLeft: <><path d="M2.5 4h11" /><path d="M2.5 8h7" /><path d="M2.5 12h9" /></>,
  alignCenter: <><path d="M2.5 4h11" /><path d="M4.5 8h7" /><path d="M3.5 12h9" /></>,
  alignRight: <><path d="M2.5 4h11" /><path d="M6.5 8h7" /><path d="M4.5 12h9" /></>,
  alignJustify: <><path d="M2.5 4h11" /><path d="M2.5 8h11" /><path d="M2.5 12h11" /></>,
  bulletList: <><circle cx="3.2" cy="4" r="1" fill="currentColor" stroke="none" /><circle cx="3.2" cy="8" r="1" fill="currentColor" stroke="none" /><circle cx="3.2" cy="12" r="1" fill="currentColor" stroke="none" /><path d="M6.5 4h7" /><path d="M6.5 8h7" /><path d="M6.5 12h7" /></>,
  orderedList: <><path d="M6.5 4h7" /><path d="M6.5 8h7" /><path d="M6.5 12h7" /><path d="M1.9 2.9h1V5" /><path d="M1.9 5h2" /><path d="M1.9 7.1h2v1.7h-2v1.7h2" /><path d="M1.9 11.2h2V14h-2" /></>,
  indent: <><path d="M6.5 4h7" /><path d="M6.5 12h7" /><path d="M2.5 8h11" /><path d="M2.2 2.9 4.4 4.6 2.2 6.3z" fill="currentColor" stroke="none" /></>,
  outdent: <><path d="M6.5 4h7" /><path d="M6.5 12h7" /><path d="M2.5 8h11" /><path d="M4.4 2.9 2.2 4.6l2.2 1.7z" fill="currentColor" stroke="none" /></>,
  table: <><rect x="2.4" y="3.4" width="11.2" height="9.2" rx="1.4" /><path d="M2.4 6.6h11.2" /><path d="M6.6 6.6v6" /></>,
  rowAfter: <><rect x="2.4" y="3.4" width="11.2" height="9.2" rx="1.4" /><path d="M2.4 8h11.2" /></>,
  columnAfter: <><rect x="2.4" y="3.4" width="11.2" height="9.2" rx="1.4" /><path d="M8 3.4v9.2" /></>,
  mergeCells: <><rect x="2.4" y="3.4" width="5" height="9.2" rx="1.2" /><rect x="8.6" y="3.4" width="5" height="9.2" rx="1.2" /></>,
  pageBreak: <><path d="M2.5 3.6h11" /><path d="M2.5 12.4h11" /><path d="M2.5 8h2.4" /><path d="M6.9 8h2.2" /><path d="M11.1 8h2.4" /></>,
  controlBlock: <><rect x="2.4" y="3.4" width="11.2" height="9.2" rx="1.4" /><path d="M2.4 6.6h11.2" strokeWidth={2.4} /><path d="M5.2 9.4h5.6" /><path d="M5.2 11.2h3.4" /></>,
  rule: <path d="M2.5 8h11" />,
  undo: <><path d="M5.6 3.4 2.6 6.4l3 3" /><path d="M2.6 6.4h6.6a3.6 3.6 0 0 1 0 7.2H6.4" /></>,
  redo: <><path d="m10.4 3.4 3 3-3 3" /><path d="M13.4 6.4H6.8a3.6 3.6 0 0 0 0 7.2h2.8" /></>,
}

export interface IconProps {
  name: IconName
  /** Pixels. 16 in a control, 18 in a rail, 20 on a tile. */
  size?: number
  /** Only for the rare icon that needs a heavier stroke than its neighbours. */
  width?: number
  style?: CSSProperties
  className?: string
}

export function Icon({ name, size = 16, width = 1.5, style, className }: IconProps) {
  const path = PATHS[name]
  // A name that is not in the set must not take the surface down with it: an
  // icon is decoration around a label, and the label is what the control means.
  if (!path) return null
  return (
    <svg
      className={className}
      style={{ display: 'block', flex: 'none', ...style }}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  )
}
